import React, { useState } from 'react'

interface CodeBlockProps {
  codeMap?: {
    pnpm?: string
    npm?: string
    yarn?: string
    bun?: string
  }
  singleCode?: string
}

export const CodeBlock: React.FC<CodeBlockProps> = ({ codeMap, singleCode }) => {
  const [activeTab, setActiveTab] = useState<'pnpm' | 'npm' | 'yarn' | 'bun'>('pnpm')
  const [copied, setCopied] = useState(false)

  const currentCode = codeMap
    ? codeMap[activeTab] || Object.values(codeMap)[0] || ''
    : singleCode || ''

  const handleCopy = () => {
    navigator.clipboard.writeText(currentCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const hasTabs = !!codeMap && Object.keys(codeMap).length > 1

  return (
    <div className="my-6 rounded-2xl border border-neutral-200/80 dark:border-neutral-800 bg-[#f8f9fa] dark:bg-neutral-900/90 shadow-xs overflow-hidden transition-all">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-neutral-200/70 dark:border-neutral-800/80 bg-neutral-100/50 dark:bg-neutral-900/50">
        <div className="flex items-center gap-3">
          {/* Terminal icon */}
          <div className="w-5 h-5 rounded-md bg-neutral-800 dark:bg-neutral-800 text-white flex items-center justify-center text-[10px] font-mono font-bold shrink-0">
            &gt;_
          </div>

          {/* Package Manager Tabs */}
          {hasTabs && (
            <div className="flex items-center gap-1">
              {(['pnpm', 'npm', 'yarn', 'bun'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-2.5 py-1 text-xs font-mono rounded-lg transition-all ${
                    activeTab === tab
                      ? 'bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white font-semibold shadow-xs border border-neutral-200/60 dark:border-neutral-700'
                      : 'text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200 font-normal'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Copy Button */}
        <button
          onClick={handleCopy}
          className="p-1.5 text-neutral-400 hover:text-neutral-700 dark:hover:text-white rounded-lg transition-colors flex items-center gap-1.5 text-xs font-mono"
          title="copy code"
        >
          {copied ? (
            <>
              <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-emerald-500 font-medium lowercase">copied</span>
            </>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          )}
        </button>
      </div>

      {/* Code Area */}
      <div className="p-5 overflow-x-auto">
        <pre className="font-mono text-sm sm:text-base text-neutral-800 dark:text-neutral-100 leading-relaxed whitespace-pre-wrap">
          <code>{currentCode}</code>
        </pre>
      </div>
    </div>
  )
}
